<template>
  <div style="width: 100%; height: 100%">
    <v-card
      class="custom-card cabinet-card"
    >
      <v-card-title
        class="d-flex flex-column align-start pt-2 pb-2"
        :style="{ backgroundColor: failureAlarmValue ? `red` : `#e3eef9` }"
      >
        <div class="table-title" v-html="formattedComponentName"></div>
      </v-card-title>

      <v-card-text
        class="mt-2"
        @click="showHistoryDialog = true"
      >
        <v-row>
          <v-col cols="12">
            <div
              class="v-label mt-4 mb-4 body-2"
              style="text-align: center"
            >
              Temperatura:
              {{ temperatureValue > 1000 ? Math.round(temperatureValue) : (temperatureValue || 0).toFixed(2) }} ºC
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-dialog
      v-if="temperatureVariable?.seconds > 0"
      v-model="showHistoryDialog"
    >
      <template>
        <v-container fluid>
          <MultiVariableLineChart
            :title="`Evolución en tiempo real de la temperatura del armario ${input.settings.component_name}`"
            realTime
            :address="temperatureVariable.formatted_address"
            @close="showHistoryDialog = false"
          ></MultiVariableLineChart>
        </v-container>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import MultiVariableLineChart from "@/components/utils/MultiVariableLineChart.vue";

export default {
  name: "QLVNCabinet",

  props: {
    input: Object,
  },

  components: { MultiVariableLineChart },

  data() {
    return {
      showHistoryDialog: false,
    };
  },

  computed: {
    ...mapState(["editing"]),

    vartable() {
      return this.actualPage().global_item_list.find((gi) => gi.settings.component_type.includes("connection"))
        .customized.vartable;
    },

    temperatureVariable() {
      return this.vartable?.find((v) => v.formatted_address == this.input.customized.variable_temperature);
    },

    temperatureValue() {
      return this.temperatureVariable?.value;
    },

    failureAlarmValue() {
      return this.vartable?.find((v) => v.formatted_address == this.input.customized.variable_failure_alarm)?.value;
    },

    // Computed para formatear el nombre del componente
    formattedComponentName() {
      if (!this.input?.settings?.component_name) return "";
      return this.input.settings.component_name.replace("[", "<br>[");
    },
  },

  methods: {
    ...mapGetters(["actualPage"]),
    ...mapActions("nodered", ["setShowingHistoryAddress"]),

    handleMediaChange() {
      const DEFAULT_MIN_COMPONENT_WIDTH = 350;
      const WIDTH_WITHOUT_MARGINS_IN_PERCENTAGE = 96;
      const MARGIN_IN_PIXELS = 15;

      const numberOfColumns = Math.floor(window.innerWidth / DEFAULT_MIN_COMPONENT_WIDTH) || 1;
      const parentDiv = Object.values(this.$parent.$refs).find((r) => r !== undefined);
      if (parentDiv && numberOfColumns === 1) {
        parentDiv.style.paddingBottom = `${MARGIN_IN_PIXELS}px`;
        parentDiv.style.paddingRight = `${MARGIN_IN_PIXELS}px`;
        parentDiv.style.width = `${WIDTH_WITHOUT_MARGINS_IN_PERCENTAGE}%`;
        parentDiv.style.height = "fit-content";
        parentDiv.style.top = `${[...this.$parent.$parent.$refs.ScadaPage.getElementsByClassName("root")].reduce(
          function (totalTop, previousCabinetRoot, previousCabinetRootIndex) {
            return (
              totalTop +
              (previousCabinetRootIndex < parentDiv.getAttribute("index")
                ? previousCabinetRoot.children[0].children[0].getBoundingClientRect().height
                : 0)
            );
          },
          0
        )}px`;
        parentDiv.style.left = `${MARGIN_IN_PIXELS}px`;
      } else if (parentDiv) {
        const cabinetIndex = parentDiv.getAttribute("index");
        const horizontalIndex = cabinetIndex % numberOfColumns;
        parentDiv.style.paddingBottom = `${MARGIN_IN_PIXELS}px`;
        parentDiv.style.paddingRight = `${MARGIN_IN_PIXELS}px`;
        parentDiv.style.width = `${
          WIDTH_WITHOUT_MARGINS_IN_PERCENTAGE / numberOfColumns -
          (numberOfColumns - 1) * (MARGIN_IN_PIXELS / window.innerWidth)
        }%`;
        const cabinetRoots = [...this.$parent.$parent.$refs.ScadaPage.getElementsByClassName("root")];

        const maxHeightInRow = Math.max(
          ...cabinetRoots
            .slice(
              cabinetIndex - (cabinetIndex % numberOfColumns),
              cabinetIndex - (cabinetIndex % numberOfColumns) + numberOfColumns
            )
            .map((a) => a.children[0].children[0].getBoundingClientRect().height)
        );

        if (maxHeightInRow > parentDiv.getBoundingClientRect().height) parentDiv.style.height = `${maxHeightInRow}px`;

        parentDiv.style.top = `${cabinetRoots.reduce(function (
          totalTop,
          _previousCabinetRoot,
          previousCabinetRootIndex
        ) {
          return (
            totalTop +
            (cabinetIndex >= numberOfColumns &&
            (previousCabinetRootIndex + 1) % numberOfColumns == 0 &&
            previousCabinetRootIndex < parentDiv.getAttribute("index")
              ? Math.max(
                  ...cabinetRoots
                    .slice(previousCabinetRootIndex - numberOfColumns + 1, previousCabinetRootIndex + 1)
                    .map((a) => a.children[0].children[0].getBoundingClientRect().height)
                )
              : 0)
          );
        },
        0)}px`;
        parentDiv.style.left = `${
          (WIDTH_WITHOUT_MARGINS_IN_PERCENTAGE / numberOfColumns) * horizontalIndex +
          ((MARGIN_IN_PIXELS / window.innerWidth) * horizontalIndex + 1)
        }%`;
      }
    },
  },

  watch: {
    showHistoryDialog(value) {
      this.setShowingHistoryAddress(value ? this.temperatureVariable.formatted_address : "");
    },
  },

  mounted() {
    window.addEventListener("resize", this.handleMediaChange);
    this.$nextTick(() => {
      [...this.$parent.$parent.$refs.ScadaPage.getElementsByClassName("root")].every(
        (a) => (a.children[0].children[0].style.height = "fit-content")
      );
      window.dispatchEvent(new Event("resize"));
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleMediaChange);
  },
};
</script>

<style scoped>
.theme--light.v-input >>> input,
.theme--light.v-input >>> textarea,
.v-text-field >>> .v-input__control {
  color: rgba(0, 0, 0, 0.6);
}

.v-label {
  font-size: 14px;
}

.cabinet-card {
  color: rgb(61, 59, 59);
  border: solid;
  border-width: 1px;
}
</style>
